import { EarningsPeriod } from '@api';
import { Card } from '@design/components/card/Card';
import { EST_TIME_ZONE, getCurrencySymbol } from '@helpers';
import { formatInTimeZone } from 'date-fns-tz';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './EarningsOverview.module.scss';
import { MetricValue } from './metric-value/MetricValue';

const REPORTING_DATE_TIME_FORMAT = "MMM d yyyy' · 'h:mmaaa";

interface OverviewItem {
  key: string;
  label: string;
  value: ReactNode;
}

export interface EarningsOverviewProps {
  activeReportingPeriod: EarningsPeriod;
}

export const EarningsOverview = ({
  activeReportingPeriod,
}: EarningsOverviewProps) => {
  const { t } = useTranslation(['earnings']);
  const { reporting_date, eps, revenue, currency, reported } =
    activeReportingPeriod;
  const currencySymbol = getCurrencySymbol(currency);

  const epsData = reported ? eps : { value: eps.estimate };
  const revenueData = reported ? revenue : { value: revenue.estimate };

  const overviewItems: OverviewItem[] = [
    {
      key: 'reportingDate',
      label: t('earnings:overview.reportingDate'),
      value: `${formatInTimeZone(
        new Date(reporting_date),
        EST_TIME_ZONE,
        REPORTING_DATE_TIME_FORMAT
      )} ET`,
    },
    {
      key: 'epsExpectations',
      label: t(
        reported ? 'earnings:overview.eps' : 'earnings:overview.epsExpectations'
      ),
      value: <MetricValue data={epsData} currencySymbol={currencySymbol} />,
    },
    {
      key: 'revenueExpectations',
      label: t(
        reported
          ? 'earnings:overview.revenue'
          : 'earnings:overview.revenueExpectations'
      ),
      value: <MetricValue data={revenueData} currencySymbol={currencySymbol} />,
    },
  ];

  return (
    <div className={styles.root} data-testid="earnings-overview">
      {overviewItems.map(({ key, label, value }) => (
        <Card
          key={key}
          size="large"
          className={styles.card}
          data-testid={`earnings-overview-card-${key}`}
        >
          <span className={styles.label}>{label}</span>
          <span className={styles.value}>{value}</span>
        </Card>
      ))}
    </div>
  );
};
